import React, { useEffect, useState } from 'react';
import { noop } from 'lodash';
import Icon from 'react-fontawesome';
import styled, { css } from 'styled-components';

import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useIsMobile from 'hooks/useIsMobile';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import CircularProgress from './CircularProgress';

export enum DataTableTheme {
  DEFAULT = 'default',
  OPEN = 'open',
}
interface TableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
}

type DataTableProps<T> = TableProps<T> & {
  defaultSortBy?: SortingState;
  desktopOnlyColumns?: string[];
  columnVisibility?: Record<string, boolean>;
  hasMore?: boolean;
  loading?: boolean;
  drillable?: boolean;
  hasFooter?: boolean;
  theme?: DataTableTheme;
  maxHeight?: string;
  freezeFirstColumn?: boolean;
  setHoveredRowId?: (hoveredRowId: string) => void;
  onRowClick?: (row: Row<T>) => void;
  onBodyEndReached?: () => void;
  rowStyle?: (row: Row<T>) => React.CSSProperties | undefined;
  // Hierarchical rows: when provided, react-table treats returned children as nested sub-rows that
  // sort within their parent group and render in flattened expanded order.
  getSubRows?: (row: T) => T[] | undefined;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
};

export default function DataTable<T>(props: DataTableProps<T>) {
  const { customRef, isIntersecting } = useIntersectionObserver();
  const {
    defaultSortBy = [],
    desktopOnlyColumns = [],
    columnVisibility = {},
    hasMore = false,
    loading,
    drillable = false,
    setHoveredRowId = noop,
    onRowClick,
    onBodyEndReached,
    rowStyle,
    hasFooter = true,
    theme = DataTableTheme.DEFAULT,
    maxHeight,
    freezeFirstColumn = false,
    getSubRows,
    expanded,
    onExpandedChange,
    ...reactTableProps
  } = props;

  const [sorting, setSorting] = useState<SortingState>(defaultSortBy);

  const isMobile = useIsMobile();

  const table = useReactTable({
    ...reactTableProps,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    ...(getSubRows ? { getSubRows, getExpandedRowModel: getExpandedRowModel(), onExpandedChange } : {}),
    state: {
      columnVisibility: {
        ...desktopOnlyColumns.reduce((acc, key) => ({ ...acc, [key]: !isMobile }), {}),
        ...columnVisibility,
      },
      sorting,
      ...(expanded !== undefined ? { expanded } : {}),
    },
  });

  useEffect(() => {
    if (isIntersecting && hasMore && !loading) {
      onBodyEndReached?.();
    }
  }, [hasMore, isIntersecting, loading, onBodyEndReached]);

  const handleRowClick = (row: Row<T>) => {
    if (drillable && onRowClick) {
      onRowClick(row);
    }
  };

  const hasTableRows = !!table.getRowModel().rows.length;

  return loading && !hasTableRows ? (
    <StyledCircularProgress />
  ) : (
    <Table $theme={theme} $maxHeight={maxHeight} $freezeFirstColumn={freezeFirstColumn}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableHeaderFooterRow key={headerGroup.id} $theme={theme}>
            {headerGroup.headers.map((header) => (
              <TableHeaderCell
                key={header.id}
                isSorted={!!header.column.getIsSorted()}
                isSortable={header.column.getCanSort()}
                onClick={header.column.getToggleSortingHandler()}
                {...header.column.columnDef.meta}
                width={header.getSize()}>
                {flexRender(header.column.columnDef.header, header.getContext())}
                {header.column.getCanSort() && (
                  <ArrowUpDownIcon
                    direction={header.column.getIsSorted() || 'none'}
                    height={10}
                    width={10}
                    style={{ marginLeft: 8 }}
                  />
                )}
              </TableHeaderCell>
            ))}
          </TableHeaderFooterRow>
        ))}
      </TableHeader>

      <TableBody>
        {!hasTableRows && (
          <TableRow>
            <TableCell colSpan={table.getVisibleFlatColumns().length} style={{ justifyContent: 'center' }}>
              No rows found
            </TableCell>
          </TableRow>
        )}
        {hasTableRows &&
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              drillable={drillable}
              style={rowStyle?.(row)}
              onMouseEnter={() => setHoveredRowId(row.id)}
              onMouseLeave={() => setHoveredRowId('')}
              onClick={() => handleRowClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} {...cell.column.columnDef.meta} width={cell.column.getSize()}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        {/* Empty row used to trigger when the end is reached */}
        <HiddenTableRow ref={customRef}></HiddenTableRow>
        {hasTableRows && loading && (
          <TableRow>
            <TableCell colSpan={table.getVisibleFlatColumns().length}>
              <StyledCircularProgress />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {hasTableRows && hasFooter && (
        <TableFooter>
          {table.getFooterGroups().map((footerGroup) => (
            <TableHeaderFooterRow key={footerGroup.id} $theme={theme}>
              {footerGroup.headers.map((footer) => (
                <TableFooterCell key={footer.id} {...footer.column.columnDef.meta} width={footer.column.getSize()}>
                  {flexRender(footer.column.columnDef.footer, footer.getContext())}
                </TableFooterCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableFooter>
      )}
    </Table>
  );
}

const StyledCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.primaryBlue};
  margin: auto;
`;

export const Table = styled.table<{ $theme: DataTableTheme; $maxHeight?: string; $freezeFirstColumn?: boolean }>`
  overflow: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  position: relative;
  ${({ $theme }) =>
    $theme === DataTableTheme.DEFAULT &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.black10};
    `}
  border-radius: 4px;
  max-height: ${({ $maxHeight }) => $maxHeight || '440px'};

  > * + * {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }

  ${({ $freezeFirstColumn }) =>
    $freezeFirstColumn &&
    css`
      /* Freeze the first cell of each row during horizontal scroll. Solid white background so
         the translucent row tints (header black03, sub-row black05, hover primaryBlue05) don't
         let scrolled cells bleed through. */
      tr > td:first-child {
        position: sticky;
        left: 0;
        z-index: 1;
        background-color: ${({ theme }) => theme.colors.white};
      }

      /* Header's sticky-left cell: solid white base + translucent black03 overlay so it
         composites to match the rest of the (translucent) header row while still masking
         scrolled cells. */
      thead tr > td:first-child {
        z-index: 2;
      }
      thead tr > td:first-child::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: ${({ theme }) => theme.colors.black03};
        z-index: -1;
        pointer-events: none;
      }
    `}
`;

export const TableHeader = styled.thead`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.white};
`;

export const TableBody = styled.tbody`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.white};

  > * + * {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }
`;

export const TableRow = styled.tr<{ drillable?: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  min-width: min-content;
  ${({ drillable = false, theme }) =>
    drillable &&
    `
  cursor: pointer;
  &:hover {
    background: ${theme.colors.primaryBlue05};
  }
    `}
`;

const HiddenTableRow = styled(TableRow)`
  border: none;
  height: 1px; /* Needs height to trigger the intersection */
`;

export const TableHeaderFooterRow = styled(TableRow)<{ $theme: DataTableTheme }>`
  flex-basis: 100%;
  ${({ $theme }) =>
    $theme === DataTableTheme.DEFAULT &&
    css`
      background-color: ${({ theme }) => theme.colors.black03};
    `}
`;

export const TableFooter = styled.tfoot`
  display: flex;
`;

export const TableSortIcon = styled(Icon)`
  padding-left: 8px;
  padding-top: 1px;
`;

export const TableCell = styled.td<{ width?: number; bold?: boolean; padding?: string }>`
  align-items: center;
  display: flex;
  flex: ${({ width }) => (width ? width : 150)} 0 auto;
  padding: ${({ padding }) => (padding ? padding : '16px 24px')};
  text-overflow: ellipsis;
  white-space: initial;
  width: ${({ width }) => (width ? width : 150)}px;
`;

export const TableFooterCell = styled(TableCell)`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.black};
`;

export const TableHeaderCell = styled(TableCell)<{ isSorted: boolean; isSortable: boolean }>`
  align-items: normal;
  color: ${(props) => (props.isSorted ? props.theme.colors.black : props.theme.colors.black50)};
  font-weight: bold;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};
`;
